import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Clock, Ban, LogOut, Package, ShoppingBag, Heart, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatBRL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/hooks/useFavorites";
import CartView from "@/components/CartView";
import AuthForm from "@/components/AuthForm";
import Reveal from "@/components/Reveal";

const STATUS = {
    paid: { label: "Pago", icon: BadgeCheck, cls: "border-green-400/50 text-green-400" },
    pending_payment: { label: "Aguardando pagamento", icon: Clock, cls: "border-forge-yellow/50 text-forge-yellow" },
    expired: { label: "Expirado", icon: Ban, cls: "border-forge-line text-forge-dim" },
};

const TABS = [
    { id: "pedidos", label: "Pedidos", testid: "perfil-tab-pedidos" },
    { id: "carrinho", label: "Carrinho", testid: "perfil-tab-carrinho" },
    { id: "favoritos", label: "Favoritos", testid: "perfil-tab-favoritos" },
];

export default function Perfil() {
    const { user, login, logout } = useAuth();
    const { add, clear } = useCart();
    const { data: favData, isFav, toggle } = useFavorites();
    const qc = useQueryClient();
    const [search, setSearch] = useSearchParams();
    const [tab, setTab] = useState("pedidos");

    const ordersQuery = useQuery({
        queryKey: ["orders"],
        enabled: !!user,
        queryFn: async () => (await api.get("/orders/mine")).data,
        refetchInterval: (q) =>
            (q.state.data?.orders || []).some((o) => o.status === "pending_payment") ? 6000 : false,
    });

    useEffect(() => {
        if (!user) return;
        if (search.get("retorno")) {
            clear();
            qc.invalidateQueries({ queryKey: ["orders"] });
            toast.success("Obrigado! Estamos confirmando seu pagamento — o status atualiza abaixo.");
            search.delete("retorno");
            setSearch(search, { replace: true });
        }
    }, [user, search, qc, setSearch]);

    if (user === null) {
        return (
            <div className="flex min-h-[70vh] items-center justify-center pt-28">
                <p className="animate-pulse font-mono text-xs uppercase tracking-[0.3em] text-forge-yellow">
                    Carregando sua conta...
                </p>
            </div>
        );
    }

    if (user === false) {
        return (
            <section className="pb-24 pt-28 md:pb-32 md:pt-40" data-testid="perfil-page">
                <div className="zf-container">
                    <div className="pt-4">
                        <AuthForm />
                    </div>
                </div>
            </section>
        );
    }

    const orders = ordersQuery.data?.orders || [];
    const favProducts = favData?.products || [];

    return (
        <section className="pb-24 pt-28 md:pb-32 md:pt-40" data-testid="perfil-page">
            <div className="zf-container">
                <Reveal>
                    <p className="zf-eyebrow">Sua conta</p>
                    <div className="mt-6 flex flex-wrap items-center justify-between gap-6 border border-forge-line bg-forge-panel p-6 md:p-8">
                        <div className="flex items-center gap-5">
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                    data-testid="perfil-avatar"
                                    className="h-16 w-16 rounded-full border-2 border-forge-yellow/70 object-cover"
                                />
                            ) : (
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forge-yellow font-display text-xl font-bold text-forge-ink">
                                    {(user.name || "?")[0]}
                                </span>
                            )}
                            <div>
                                <h1 className="font-display text-xl font-bold uppercase text-forge-paper md:text-2xl" data-testid="perfil-name">
                                    {user.name || "Cliente"}
                                </h1>
                                <p className="mt-1 text-sm text-forge-dim" data-testid="perfil-email">{user.email}</p>
                            </div>
                        </div>
                        <button
                            onClick={logout}
                            data-testid="perfil-logout-button"
                            className="inline-flex items-center gap-2 border border-forge-line px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-forge-dim transition-colors duration-300 hover:border-red-500 hover:text-red-400"
                        >
                            <LogOut className="h-4 w-4" />
                            Sair
                        </button>
                    </div>
                </Reveal>

                <Reveal delay={0.06}>
                    <div className="mt-10 flex gap-6 border-b border-forge-line" data-testid="perfil-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={t.testid}
                                className={`relative pb-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                                    tab === t.id ? "text-forge-yellow" : "text-forge-dim hover:text-forge-paper"
                                }`}
                            >
                                {t.label}
                                {tab === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-forge-yellow" />}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className="mt-8">
                    {tab === "pedidos" && (
                        <div data-testid="perfil-orders-panel">
                            {ordersQuery.isLoading ? (
                                <div className="space-y-4">
                                    {[0, 1].map((i) => (
                                        <div key={i} className="h-24 animate-pulse bg-forge-panel" />
                                    ))}
                                </div>
                            ) : orders.length === 0 ? (
                                <div
                                    className="layer-lines flex min-h-[220px] flex-col items-center justify-center gap-3 border border-dashed border-forge-line bg-forge-panel/40 p-10 text-center"
                                    data-testid="perfil-orders-empty"
                                >
                                    <Package className="h-8 w-8 text-forge-yellow/60" />
                                    <p className="font-display text-sm font-bold uppercase text-forge-paper">
                                        Nenhum pedido ainda
                                    </p>
                                    <p className="max-w-xs text-xs leading-relaxed text-forge-dim">
                                        Quando você comprar, o pedido e o status do pagamento aparecem aqui.
                                    </p>
                                    <Link
                                        to="/catalogo"
                                        data-testid="perfil-orders-catalog-link"
                                        className="mt-2 inline-flex items-center gap-2 bg-forge-yellow px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5"
                                    >
                                        <ShoppingBag className="h-4 w-4" />
                                        Ver catálogo
                                    </Link>
                                </div>
                            ) : (
                                <div className="space-y-4" data-testid="perfil-orders-list">
                                    {orders.map((o) => {
                                        const st = STATUS[o.status] || STATUS.expired;
                                        return (
                                            <div
                                                key={o.id}
                                                data-testid={`order-card-${o.id}`}
                                                className="flex flex-wrap items-center justify-between gap-4 border border-forge-line bg-forge-panel p-5"
                                            >
                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${st.cls}`}>
                                                            <st.icon className="h-3 w-3" />
                                                            {st.label}
                                                        </span>
                                                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-forge-dim">
                                                            {new Date(o.created_at).toLocaleDateString("pt-BR")}
                                                        </span>
                                                    </div>
                                                    <p className="mt-2 truncate text-sm text-forge-paper">
                                                        {o.items.map((i) => `${i.quantity}x ${i.name}`).join(" · ")}
                                                    </p>
                                                </div>
                                                <p className="font-display text-base font-bold text-forge-yellow" data-testid={`order-total-${o.id}`}>
                                                    {formatBRL(o.total_cents)}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "carrinho" && (
                        <div data-testid="perfil-cart-panel">
                            <CartView testPrefix="perfil-cart" />
                        </div>
                    )}

                    {tab === "favoritos" && (
                        <div data-testid="perfil-favorites-panel">
                            {favProducts.length === 0 ? (
                                <div
                                    className="layer-lines flex min-h-[220px] flex-col items-center justify-center gap-3 border border-dashed border-forge-line bg-forge-panel/40 p-10 text-center"
                                    data-testid="perfil-favorites-empty"
                                >
                                    <Heart className="h-8 w-8 text-forge-yellow/60" />
                                    <p className="font-display text-sm font-bold uppercase text-forge-paper">
                                        Nenhum favorito ainda
                                    </p>
                                    <p className="max-w-xs text-xs leading-relaxed text-forge-dim">
                                        Toque no coração das peças no catálogo para salvá-las aqui.
                                    </p>
                                    <Link
                                        to="/catalogo"
                                        data-testid="perfil-favorites-catalog-link"
                                        className="mt-2 inline-flex items-center gap-2 bg-forge-yellow px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5"
                                    >
                                        Ver catálogo
                                    </Link>
                                </div>
                            ) : (
                                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="perfil-favorites-grid">
                                    {favProducts.map((p) => (
                                        <article
                                            key={p.id}
                                            data-testid={`favorite-card-${p.id}`}
                                            className="group relative aspect-square overflow-hidden border border-forge-line bg-forge-panel transition-colors duration-300 hover:border-forge-yellow/70"
                                        >
                                            <img
                                                src={fileUrl(p.storage_path)}
                                                alt={p.name}
                                                loading="lazy"
                                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                                            <button
                                                onClick={() => toggle(p)}
                                                data-testid={`favorite-remove-${p.id}`}
                                                aria-label={`Remover ${p.name} dos favoritos`}
                                                className="absolute left-4 top-4 z-20 flex h-9 w-9 items-center justify-center border border-white/20 bg-forge-ink/80 text-forge-yellow backdrop-blur-sm transition-all duration-300 hover:scale-110"
                                            >
                                                <Heart className="h-4 w-4 fill-forge-yellow" />
                                            </button>
                                            {p.price_cents > 0 && (
                                                <button
                                                    onClick={() => {
                                                        add(p);
                                                        toast.success(`${p.name} foi para o carrinho.`);
                                                    }}
                                                    data-testid={`favorite-add-cart-${p.id}`}
                                                    aria-label={`Adicionar ${p.name} ao carrinho`}
                                                    className="absolute bottom-5 right-5 z-20 flex h-11 w-11 items-center justify-center bg-forge-yellow text-forge-ink transition-all duration-300 hover:scale-110"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                            )}
                                            <div className="pointer-events-none absolute inset-x-0 bottom-0 p-6 pr-16">
                                                <h3 className="font-display text-lg font-bold uppercase text-forge-paper">
                                                    {p.name}
                                                </h3>
                                                {p.price_cents > 0 && (
                                                    <p className="mt-1 font-mono text-xs text-forge-yellow">{formatBRL(p.price_cents)}</p>
                                                )}
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
